/**
 * ShareButton — opens a modal with a generated share image, the
 * share text, and quick actions (download, copy, post on X).
 *
 * Extracted from TokenDetailsPage so the V2 layout can reuse it.
 * Accepts a minimal token-details shape (name/symbol/safety/warnings)
 * — anything richer is passed through to generateSocialMediaMessage
 * which is already tolerant of the two naming conventions in use.
 */
import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { apiFetch, getApiHost } from '../utils/api';
import { trackEvent } from '../utils/analytics';
import { generateSocialMediaMessage } from '../utils/riskUtils';

interface Props {
  mint: string;
  /** Token page path when it isn't /token/<mint> (EVM: /token/<chain>/<0x…>). */
  pagePath?: string;
  // Intentionally loose — generateSocialMediaMessage is tolerant of
  // both the safety-analysis and holder-analysis field naming, so we
  // just forward whatever the caller has.
  tokenDetails: any;
  className?: string;
}

const ShareButton: React.FC<Props> = ({ mint, tokenDetails, className, pagePath }) => {
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleShare = async () => {
    trackEvent('share_modal_opened', { mint });
    setLoading(true);
    try {
      const imageUrl = `${getApiHost()}/api/share/image/${mint}`;
      const response = await apiFetch(imageUrl);
      if (!response.ok) throw new Error('Failed to generate image');
      const blob = await response.blob();
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (err) {
      console.error('Share image error:', err);
    } finally {
      setLoading(false);
    }
  };

  const shareText = generateSocialMediaMessage(tokenDetails as any);
  const pageUrl = `${window.location.origin}${pagePath ?? `/token/${mint}`}`;

  const handleDownload = () => {
    if (!previewUrl) return;
    const a = document.createElement('a');
    a.href = previewUrl;
    a.download = `deepnets-${mint.slice(0, 8)}.png`;
    a.click();
  };

  const handleCopyImage = async () => {
    if (!previewUrl) return;
    try {
      const response = await fetch(previewUrl);
      const blob = await response.blob();
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const handlePostToX = (source: 'direct' | 'modal') => {
    trackEvent('post_on_x_clicked', { mint, source });
    // Share the canonical token URL — the Netlify edge function sees the
    // Twitterbot UA and proxies the OG meta tags through, so X renders
    // the card without needing the ugly /api/share/page URL anymore.
    const tokenPageUrl = `${window.location.origin}${pagePath ?? `/token/${mint}`}`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(tokenPageUrl)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const closeModal = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setCopied(false);
  };

  return (
    <>
      <div className={className ?? 'flex items-center gap-1.5'}>
        {/* Primary: ONE TAP straight to X — no modal, no waiting on the share
            image (the crawler renders the card from the URL when it posts).
            This is the friction that was killing the share loop. */}
        <button
          onClick={() => handlePostToX('direct')}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 transition-all text-gray-300 hover:text-white text-sm"
          title="Share on X"
        >
          <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current" aria-hidden="true">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
          </svg>
          <span>Share on X</span>
        </button>
        {/* Secondary: the rich card (downloadable image, copy, other platforms). */}
        <button
          onClick={handleShare}
          disabled={loading}
          className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 transition-all text-gray-400 hover:text-white disabled:opacity-50"
          title="Share card / more options"
        >
          {loading ? (
            <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
          ) : (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
            </svg>
          )}
        </button>
      </div>

      {previewUrl && ReactDOM.createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div className="relative max-w-[90vw]">
            <button
              onClick={closeModal}
              className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-gray-800 border border-gray-600 flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-700 transition-all z-10"
            >
              ✕
            </button>

            <img
              src={previewUrl}
              alt="Share preview"
              className="rounded-xl shadow-2xl border border-gray-700 max-w-full max-h-[60vh] object-contain"
            />

            <div className="mt-3 bg-white/5 border border-white/10 rounded-lg p-3 max-w-full">
              <p className="text-gray-300 text-sm whitespace-pre-wrap">{shareText}</p>
              <p className="text-gray-500 text-xs mt-1">{pageUrl}</p>
            </div>

            <div className="flex items-center justify-center space-x-3 mt-4">
              <button
                onClick={handleDownload}
                className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20 text-white text-sm transition-all"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                <span>Download</span>
              </button>
              <button
                onClick={handleCopyImage}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg border text-sm transition-all ${
                  copied
                    ? 'bg-green-500/20 border-green-500/30 text-green-400'
                    : 'bg-white/10 hover:bg-white/20 border-white/20 text-white'
                }`}
              >
                {copied ? (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <span>Copy Image</span>
                  </>
                )}
              </button>
              <button
                onClick={() => handlePostToX('modal')}
                className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20 text-white text-sm transition-all"
              >
                <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" aria-hidden="true">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
                <span>Post on X</span>
              </button>
            </div>

            <p className="mt-3 text-[11px] text-gray-500 text-center max-w-md mx-auto leading-relaxed">
              <span className="text-gray-400">Tip:</span> if X doesn't show the card preview right away,
              close the post window and click Post on X again. X's preview queue can take ~20 seconds
              the first time it sees a link.
            </p>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default ShareButton;
